'use client'

import { useEffect, useId, useState } from 'react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { ArrowLeftRight } from 'lucide-react'

/**
 * Logo « BLC Médias » CLIQUABLE — bascule entre les deux apps :
 *   BLC Music (/app)  ⇄  BLC Video Player (/app/video)
 *
 * Carte à 2 faces (3D) : la face avant = l'app active, la face arrière = l'autre.
 *  - Au survol / focus : la carte se retourne → on voit où on va.
 *  - Au repos : un petit « coup d'œil » périodique (la carte pivote à moitié)
 *    + une pastille ⇄ qui pulse → l'utilisateur comprend que c'est cliquable.
 *  - prefers-reduced-motion : aucune animation, seulement la pastille.
 * `compact` : icône seule (sidebar portrait, header mobile).
 */
type App = 'music' | 'video'

const APPS: Record<App, { name: string; tagline: string; href: string; from: string; via: string; to: string }> = {
  music: { name: 'BLC Music', tagline: 'Écoute. Ressens. Partage.', href: '/app',       from: '#3FA8FF', via: '#7A4DFF', to: '#FF3D7F' },
  video: { name: 'BLC Video', tagline: 'Regarde. Vibre. Partage.',  href: '/app/video', from: '#FF8A3D', via: '#FF2D55', to: '#B3124A' },
}

function Mark({ app, size }: { app: App; size: number }) {
  const a = APPS[app]
  // id UNIQUE par instance : le logo existe en plusieurs exemplaires (barre latérale
  // masquée sur mobile + en-tête) et un dégradé défini dans un élément masqué
  // (display:none) rend invisible tout SVG qui le référence.
  const id = `blcmk-${app}-${useId().replace(/:/g, '')}`
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden className="shrink-0 drop-shadow-[0_4px_14px_rgb(var(--a2)/.45)]">
      <defs>
        <linearGradient id={id} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor={a.from} /><stop offset=".5" stopColor={a.via} /><stop offset="1" stopColor={a.to} />
        </linearGradient>
      </defs>
      {app === 'music' ? (
        <>
          <path d="M8 5 42 24 8 43Z" fill={`url(#${id})`} />
          <path d="M16 16 30 24 16 32Z" fill="#0A0B20" />
        </>
      ) : (
        <>
          <rect x="3" y="9" width="42" height="30" rx="9" fill={`url(#${id})`} />
          <path d="M20 17 31 24 20 31Z" fill="#fff" />
        </>
      )}
    </svg>
  )
}

function Face({ app, compact, back }: { app: App; compact?: boolean; back?: boolean }) {
  const a = APPS[app]
  return (
    <span className={`blc-brand-face ${back ? 'blc-brand-back' : ''} flex items-center gap-3`}>
      <Mark app={app} size={compact ? 40 : 46} />
      {!compact && (
        <span className="min-w-0 text-left leading-none">
          <span className="block text-[10px] font-semibold tracking-[0.22em] text-white/40 mb-1">BLC MÉDIAS</span>
          <span className="block text-[23px] font-extrabold tracking-tight text-white whitespace-nowrap">{a.name}</span>
          <span className="block text-[11px] text-white/50 mt-1 whitespace-nowrap">
            {back ? 'Cliquer pour y aller →' : a.tagline}
          </span>
        </span>
      )}
    </span>
  )
}

export default function BrandSwitch({ compact = false, className = '' }: { compact?: boolean; className?: string }) {
  const pathname = usePathname()
  const [last, setLast] = useState<App>('music')
  useEffect(() => { try { if (localStorage.getItem('blc.app') === 'video') setLast('video') } catch {} }, [pathname])
  const shared = /^\/app\/(settings|favorites|pro|profile|party|join|pip-window|extension)(?:\/|$)/.test(pathname || '')
  const current: App = pathname?.startsWith('/app/video') || (shared && last === 'video') ? 'video' : 'music'
  const other: App = current === 'music' ? 'video' : 'music'
  const label = `Passer à ${other === 'video' ? 'BLC Video Player' : 'BLC Music'}`

  return (
    <Link
      href={APPS[other].href}
      title={label}
      aria-label={`${APPS[current].name} — ${label}`}
      className={`blc-brand group relative block rounded-2xl outline-none focus-visible:ring-2 focus-visible:ring-a3 ${compact ? 'p-1' : 'px-2 py-2'} ${className}`}
    >
      <span className="blc-brand-card relative block">
        <Face app={current} compact={compact} />
        <Face app={other} compact={compact} back />
      </span>
      {/* Pastille ⇄ : indique qu'on peut changer d'app */}
      <span className={`blc-brand-hint absolute flex items-center justify-center rounded-full text-white
        ${compact ? 'w-5 h-5 -right-0.5 -bottom-0.5' : 'w-6 h-6 left-[34px] top-[38px]'}
        ${other === 'video' ? 'bg-[#FF2D55]' : 'bg-a2'}`}>
        <ArrowLeftRight size={compact ? 10 : 12} strokeWidth={2.6} />
      </span>
    </Link>
  )
}
